/**
 * O que fazer quando a renovação da sessão falha — GUPM-336.
 *
 * O auth-js, quando o refresh token é recusado, só apaga a sessão se o access token
 * já venceu PELO RELÓGIO DO PC. Numa estação atrasada ele acha que ainda vale e
 * preserva uma sessão morta: toda leitura volta 401 e a tela fica em "a sessão está
 * sendo renovada" para sempre. Aqui, recusa explícita do servidor encerra a sessão
 * deste navegador e a pessoa volta ao login.
 *
 * Encerramento `scope: 'local'`: o global revoga as sessões da conta em todas as
 * estações (GUPM-334).
 */
import { isAuthApiError, isAuthRefreshDiscardedError, isAuthSessionMissingError } from '@supabase/supabase-js';

/** `recusada` = o servidor disse que a sessão acabou; `transitoria` = vale tentar de novo. */
export type FalhaDaRenovacao = 'recusada' | 'transitoria';

export function classificarFalhaDaRenovacao(erro: unknown): FalhaDaRenovacao {
  if (isAuthSessionMissingError(erro)) return 'recusada';
  // 4xx do GoTrue = refresh token inválido, revogado ou reusado. 429 (limite por IP,
  // o posto compartilha um) e 5xx passam. Rede fora e renovação descartada por outra
  // aba (409, não é AuthApiError) também.
  if (isAuthApiError(erro) && erro.status >= 400 && erro.status < 500 && erro.status !== 429) {
    return 'recusada';
  }
  return 'transitoria';
}

type SessaoComToken = { access_token: string } | null;

/** Recorte do `SupabaseAuthClient` — injetável em teste. */
export interface AuthDoRenovador {
  refreshSession(): Promise<{ data: { session: SessaoComToken }; error: unknown }>;
  getSession(): Promise<{ data: { session: SessaoComToken } }>;
  signOut(opcoes: { scope: 'local' }): Promise<unknown>;
}

/** Encerra a sessão deste navegador se a falha for recusa do servidor. */
export async function encerrarSeRecusada(
  auth: Pick<AuthDoRenovador, 'signOut'>,
  erro: unknown,
): Promise<FalhaDaRenovacao> {
  const falha = classificarFalhaDaRenovacao(erro);
  if (falha === 'recusada') {
    console.warn('[sessao] renovação recusada pelo servidor — encerrando a sessão deste navegador');
    await auth.signOut({ scope: 'local' });
  }
  return falha;
}

/**
 * Renovação usada pelo `fetch-com-renovacao`, depois que o PostgREST já recusou o
 * JWT. Devolve o access token para repetir a requisição, ou `null`.
 */
export function criarRenovadorDaSessao(auth: AuthDoRenovador): () => Promise<string | null> {
  return async () => {
    const { data, error } = await auth.refreshSession();
    if (data.session) return data.session.access_token;
    // Outra aba renovou no meio: o token dela já está no storage.
    if (isAuthRefreshDiscardedError(error)) return (await auth.getSession()).data.session?.access_token ?? null;
    await encerrarSeRecusada(auth, error);
    return null;
  };
}
